"use client";

import { type ReactNode, type ComponentProps } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { SelectableCard } from "@/components/ui/selectable-card";
import type { DimensionUnit } from "@convex/schemas/validators/dimensions";

interface WizardFieldProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
  id?: string;
}

export function WizardField({
  label,
  required,
  optional,
  hint,
  error,
  children,
  className,
  id,
}: WizardFieldProps) {
  return (
    <Field controlId={id} className={cn("gap-2", className)}>
      <FieldLabel
        className="text-sm text-foreground"
        required={required}
        optional={optional}
      >
        {label}
      </FieldLabel>
      {children}
      <FieldError className="text-sm">{error}</FieldError>
      {hint && !error && (
        <FieldDescription className="text-sm">{hint}</FieldDescription>
      )}
    </Field>
  );
}

interface WizardSectionProps {
  children: ReactNode;
  className?: string;
}

export function WizardSection({ children, className }: WizardSectionProps) {
  return <div className={cn("space-y-6", className)}>{children}</div>;
}

interface WizardInputProps extends ComponentProps<typeof Input> {
  className?: string;
}

export function WizardInput({ className, ...props }: WizardInputProps) {
  return <Input className={cn("text-base", className)} {...props} size="default" />;
}

type OptionCardProps = Omit<
  ComponentProps<typeof SelectableCard>,
  "children" | "isSelected" | "layout" | "size"
> & {
  selected: boolean;
  icon: ReactNode;
  label: string;
  description?: string;
};

export function OptionCard({
  selected,
  icon,
  label,
  description,
  ...buttonProps
}: OptionCardProps) {
  return (
    <SelectableCard
      {...buttonProps}
      isSelected={selected}
      aria-pressed={selected}
    >
      <div
        className={cn(
          "flex size-12 items-center justify-center rounded-xl transition-colors",
          selected
            ? "bg-action-fill text-action-fill-foreground"
            : "bg-muted text-muted-foreground group-hover:text-foreground",
        )}
      >
        {icon}
      </div>
      <div className="text-center">
        <span className="block text-sm font-medium leading-tight text-foreground">
          {label}
        </span>
        {description && (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {description}
          </span>
        )}
      </div>
    </SelectableCard>
  );
}

interface OptionPillProps {
  id?: string;
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}

export function OptionPill({
  id,
  selected,
  onClick,
  children,
  className,
}: OptionPillProps) {
  return (
    <Button
      id={id}
      type="button"
      variant="outline"
      size="sm"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        selected && "border-action hover:bg-action-tint bg-action-tint text-action",
        className,
      )}
    >
      {children}
    </Button>
  );
}

const DIMENSION_UNIT_OPTIONS: Array<{
  label: string;
  value: DimensionUnit;
  abbrev: string;
}> = [
  { label: "Inches", value: "in", abbrev: "in" },
  { label: "Centimeters", value: "cm", abbrev: "cm" },
  { label: "Millimeters", value: "mm", abbrev: "mm" },
  { label: "Feet", value: "ft", abbrev: "ft" },
];

interface DimensionInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  unit: DimensionUnit;
  optional?: boolean;
  className?: string;
}

function DimensionInput({
  id,
  label,
  value,
  onChange,
  unit,
  optional = false,
  className,
}: DimensionInputProps) {
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <div className={cn("flex-1 min-w-0", className)}>
      <Label className="mb-1.5 block" size="sm" tone="muted">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          min={0}
          step="any"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          className={cn("text-base pr-10", optional && "border-dashed")}
          aria-label={label} size="default"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
          {unit}
        </span>
      </div>
    </div>
  );
}

interface DimensionInputGroupProps {
  length: string;
  width: string;
  height: string;
  onLengthChange: (value: string) => void;
  onWidthChange: (value: string) => void;
  onHeightChange: (value: string) => void;
  unit: DimensionUnit;
  onUnitChange: (unit: DimensionUnit) => void;
  heightOptional?: boolean;
}

function UnitToggle({
  unit,
  onUnitChange,
}: {
  unit: DimensionUnit;
  onUnitChange: (unit: DimensionUnit) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Dimension unit"
      className="flex items-center border rounded-lg overflow-hidden shrink-0 h-12 self-end"
    >
      {DIMENSION_UNIT_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onUnitChange(option.value)}
          aria-pressed={unit === option.value}
          aria-label={option.label}
          className={cn(
            "px-3 h-full text-sm font-medium transition-colors",
            unit === option.value
              ? "bg-action-fill text-action-fill-foreground"
              : "bg-background text-muted-foreground hover:text-foreground",
          )}
        >
          {option.abbrev}
        </button>
      ))}
    </div>
  );
}

export function DimensionInputGroup({
  length,
  width,
  height,
  onLengthChange,
  onWidthChange,
  onHeightChange,
  unit,
  onUnitChange,
  heightOptional = false,
}: DimensionInputGroupProps) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex flex-1 gap-3">
          <DimensionInput
            id="dimensionLength"
            label="Length"
            value={length}
            onChange={onLengthChange}
            unit={unit}
          />
          <DimensionInput
            id="dimensionWidth"
            label="Width"
            value={width}
            onChange={onWidthChange}
            unit={unit}
          />
          <DimensionInput
            id="dimensionHeight"
            label="Height"
            value={height}
            onChange={onHeightChange}
            unit={unit}
            optional={heightOptional}
          />
        </div>
        <UnitToggle unit={unit} onUnitChange={onUnitChange} />
      </div>
      {heightOptional && (
        <p className="text-xs text-muted-foreground">Height is optional</p>
      )}
    </div>
  );
}

interface WizardGridProps {
  columns?: 2 | 3 | 4;
  children: ReactNode;
  className?: string;
}

export function WizardGrid({
  columns = 2,
  children,
  className,
}: WizardGridProps) {
  return (
    <div
      className={cn(
        "grid gap-3",
        columns === 2 && "grid-cols-2",
        columns === 3 && "grid-cols-2 sm:grid-cols-3",
        columns === 4 && "grid-cols-2 sm:grid-cols-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface WizardRowProps {
  children: ReactNode;
  className?: string;
}

export function WizardRow({ children, className }: WizardRowProps) {
  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-4", className)}>
      {children}
    </div>
  );
}
